import {
  BrowserRouter,
  Routes,
  Route,
} from "react-router-dom";

/* =========================
   PUBLIC PAGES
========================= */
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import NotFound from "./pages/NotFound";
import PrivacyNotice from "./pages/PrivacyNotice";


/* =========================
   FARMER PAGES
========================= */
import FarmerDashboard from "./pages/FarmerDashboard";
import Listings from "./pages/Listings";
import Post from "./pages/Post";
import Messages from "./pages/Messages";
import Profile from "./pages/Profile";
import FarmerTransactions from "./pages/FarmerTransactions";
import FarmerNotifications from "./pages/FarmerNotifications";
import AccountSettings from "./pages/AccountSettings";
import FarmerLayout from "./components/FarmerLayout";

/* =========================
   ADMIN PAGES
========================= */
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminStaff from "./pages/AdminStaff";
import AdminListings from "./pages/AdminListings";
import AdminVerification from "./pages/AdminVerification";
import AdminTransactions from "./pages/AdminTransactions";
import AdminReports from "./pages/AdminReports";
import AdminNotifications from "./pages/AdminNotifications";
import AdminSettings from "./pages/AdminSettings";
import AdminMapMonitoring from "./pages/AdminMapMonitoring";
import AdminLayout from "./components/AdminLayout";
import RequireAdminRole from "./components/RequireAdminRole";
import RequireRole from "./components/RequireRole";

/* =========================
   BUYER PAGES
========================= */
import BuyerDashboard from "./pages/BuyerDashboard";

/* FUTURE BUYER PAGES */
import Marketplace from "./pages/Marketplace";
import BuyerFavorites from "./pages/BuyerFavorites";
import BuyerTransactions from "./pages/BuyerTransactions";
import BuyerNotifications from "./pages/BuyerNotifications";
import BuyerMapExplorer from "./pages/BuyerMap";
import BuyerProfile from "./pages/BuyerProfile";
import SellerProfile from "./pages/SellerProfile";
import BuyerLayout from "./components/BuyerLayout";
import ConnectionStatus from "./components/ConnectionStatus";

function App() {

  return (

    <BrowserRouter>

      <ConnectionStatus />

      <Routes>

        {/* =========================
            PUBLIC ROUTES
        ========================= */}

        <Route
          path="/"
          element={<LandingPage />}
        />

        <Route
          path="/privacy"
          element={<PrivacyNotice />}
        />

        <Route
          path="/login"
          element={<Login />}
        />

        <Route
          path="/register"
          element={<Register />}
        />

        <Route
          path="/forgot-password"
          element={<ForgotPassword />}
        />

        <Route
          path="/reset-password"
          element={<ResetPassword />}
        />

        <Route
          path="/onboarding"
          element={<Onboarding />}
        />

        {/* =========================
            FARMER ROUTES
            (nested under FarmerLayout so the sidebar persists
            across navigation instead of remounting per page)
        ========================= */}

        <Route
          element={
            <RequireRole roles={["farmer"]}>
              <FarmerLayout />
            </RequireRole>
          }
        >
          <Route
            path="/farmer-dashboard"
            element={<FarmerDashboard />}
          />

          <Route
            path="/listings"
            element={<Listings />}
          />

          <Route
            path="/post"
            element={<Post />}
          />

          <Route
            path="/post/:id"
            element={<Post />}
          />

          <Route
            path="/profile"
            element={<Profile />}
          />

          <Route
            path="/farmer-messages"
            element={<Messages />}
          />

          <Route
            path="/farmer-transactions"
            element={<FarmerTransactions />}
          />

          <Route
            path="/farmer-notifications"
            element={<FarmerNotifications />}
          />

          <Route
            path="/farmer-settings"
            element={<AccountSettings role="farmer" />}
          />
        </Route>

        {/* =========================
            ADMIN ROUTES
            (nested under AdminLayout so the sidebar persists
            across navigation instead of remounting per page)
        ========================= */}

        <Route
          element={
            <RequireRole roles={["admin", "mao_staff"]}>
              <AdminLayout />
            </RequireRole>
          }
        >
          <Route
            path="/admin-account"
            element={
              <RequireAdminRole>
                <AccountSettings role="admin" />
              </RequireAdminRole>
            }
          />

          <Route
            path="/admin-dashboard"
            element={<AdminDashboard />}
          />

          <Route
            path="/admin-users"
            element={
              <RequireAdminRole>
                <AdminUsers />
              </RequireAdminRole>
            }
          />

          <Route
            path="/admin-staff"
            element={
              <RequireAdminRole>
                <AdminStaff />
              </RequireAdminRole>
            }
          />

          <Route
            path="/admin-listings"
            element={<AdminListings />}
          />

          <Route
            path="/admin-verification"
            element={<AdminVerification />}
          />

          <Route
            path="/admin-transactions"
            element={<AdminTransactions />}
          />

          <Route
            path="/admin-reports"
            element={<AdminReports />}
          />

          <Route
            path="/admin-notifications"
            element={<AdminNotifications />}
          />

          <Route
            path="/admin-settings"
            element={
              <RequireAdminRole>
                <AdminSettings />
              </RequireAdminRole>
            }
          />

          <Route
            path="/admin-map"
            element={<AdminMapMonitoring />}
          />
        </Route>

        {/* =========================
            BUYER ROUTES
            (nested under BuyerLayout so the sidebar persists
            across navigation instead of remounting per page)
        ========================= */}

        <Route
          element={
            <RequireRole roles={["buyer"]}>
              <BuyerLayout />
            </RequireRole>
          }
        >
          <Route
            path="/buyer-dashboard"
            element={<BuyerDashboard />}
          />

          <Route
            path="/marketplace"
            element={<Marketplace />}
          />

          <Route
            path="/buyer-favorites"
            element={<BuyerFavorites />}
          />

          <Route
            path="/buyer-messages"
            element={<Messages />}
          />

          <Route
            path="/buyer-transactions"
            element={<BuyerTransactions />}
          />

          <Route
            path="/buyer-notifications"
            element={<BuyerNotifications />}
          />

          <Route
            path="/buyer-map"
            element={<BuyerMapExplorer />}
          />

          <Route
            path="/buyer-profile"
            element={<BuyerProfile />}
          />

          <Route
            path="/buyer-settings"
            element={<AccountSettings role="buyer" />}
          />

          <Route
            path="/seller/:id"
            element={<SellerProfile />}
          />
        </Route>

        {/* =========================
            FALLBACK
        ========================= */}

        <Route
          path="*"
          element={<NotFound />}
        />

      </Routes>

    </BrowserRouter>

  );
}

export default App;